// Discussões com histórico (vai-e-volta). Dois tipos:
//  • 'encaminhamento' — ligado a um projeto + responsável (sai da reunião/@menção);
//  • 'assunto'        — tema avulso, fora de projetos (menu "Outros assuntos").
// Cabeçalho em meeting_actions; mensagens do fio em discussion_messages.
// Ciclo: 'aberto' (em discussão) → 'encerrado' (admin/quem abriu finaliza; admin reabre).
import { supabase } from './supabase';
import { createNotifications } from './notifications';

export type DiscussionKind = 'encaminhamento' | 'assunto';
export type DiscussionStatus = 'aberto' | 'encerrado';

export interface DiscussionMessage {
  id: string;
  authorId: string | null;
  authorName: string | null;
  body: string;
  attachmentUrl: string | null;
  attachmentName: string | null;
  createdAt: string;
}

export interface Discussion {
  id: string;
  orgId: string;
  kind: DiscussionKind;
  title: string;
  productId: string | null;
  productName: string | null; // "MARCA · Nome" do projeto (exibição e link)
  createdById: string | null;
  createdByName: string | null;
  assignedToId: string | null;
  assignedName: string | null; // nomes dos participantes (exibição)
  participantIds: string[]; // todos os marcados no fio (podem comentar/acompanhar)
  status: DiscussionStatus;
  createdAt: string;
  closedAt: string | null;
  messages: DiscussionMessage[];
}

// Compat: código antigo importa "Encaminhamento".
export type Encaminhamento = Discussion;

const HCOLS =
  'id, org_id, kind, title, product_id, created_by, created_by_name, assigned_to, assigned_name, participant_ids, status, created_at, closed_at, products(name, brands(name))';

function rel<T>(v: unknown): T | null {
  if (Array.isArray(v)) return (v[0] as T) ?? null;
  return (v as T) ?? null;
}

function mapHeader(r: Record<string, unknown>): Discussion {
  const prod = rel<{ name?: string; brands?: unknown }>(r.products);
  const brand = prod ? rel<{ name?: string }>(prod.brands) : null;
  const productName = prod?.name
    ? `${brand?.name ? `${brand.name} · ` : ''}${prod.name}`
    : null;
  return {
    id: r.id as string,
    orgId: r.org_id as string,
    kind: (r.kind as DiscussionKind) ?? 'encaminhamento',
    title: r.title as string,
    productId: (r.product_id as string) ?? null,
    productName,
    createdById: (r.created_by as string) ?? null,
    createdByName: (r.created_by_name as string) ?? null,
    assignedToId: (r.assigned_to as string) ?? null,
    assignedName: (r.assigned_name as string) ?? null,
    participantIds: (r.participant_ids as string[]) ?? [],
    status: (r.status as DiscussionStatus) ?? 'aberto',
    createdAt: r.created_at as string,
    closedAt: (r.closed_at as string) ?? null,
    messages: [],
  };
}
function mapMsg(r: Record<string, unknown>): DiscussionMessage {
  return {
    id: r.id as string,
    authorId: (r.author_id as string) ?? null,
    authorName: (r.author_name as string) ?? null,
    body: r.body as string,
    attachmentUrl: (r.attachment_url as string) ?? null,
    attachmentName: (r.attachment_name as string) ?? null,
    createdAt: r.created_at as string,
  };
}

async function attachMessages(headers: Discussion[]): Promise<Discussion[]> {
  if (!headers.length) return headers;
  const ids = headers.map((h) => h.id);
  const { data } = await supabase
    .from('discussion_messages')
    .select('id, action_id, author_id, author_name, body, attachment_url, attachment_name, created_at')
    .in('action_id', ids)
    .order('created_at', { ascending: true });
  const byAction = new Map<string, DiscussionMessage[]>();
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    const list = byAction.get(r.action_id as string) ?? [];
    list.push(mapMsg(r));
    byAction.set(r.action_id as string, list);
  }
  return headers.map((h) => ({ ...h, messages: byAction.get(h.id) ?? [] }));
}

async function insertMessage(params: {
  orgId: string;
  actionId: string;
  authorId: string | null;
  authorName: string | null;
  body: string;
  attachmentUrl?: string | null;
  attachmentName?: string | null;
}): Promise<{ error?: string; id?: string }> {
  const { data, error } = await supabase
    .from('discussion_messages')
    .insert({
      org_id: params.orgId,
      action_id: params.actionId,
      author_id: params.authorId,
      author_name: params.authorName,
      body: params.body,
      attachment_url: params.attachmentUrl ?? null,
      attachment_name: params.attachmentName ?? null,
    })
    .select('id')
    .single();
  return { error: error?.message, id: (data as { id: string } | null)?.id };
}

function notify(rows: { orgId: string; userIds: (string | null)[]; productId: string | null; fromName: string | null; text: string }) {
  const uniq = [...new Set(rows.userIds.filter((x): x is string => Boolean(x)))];
  if (!uniq.length) return Promise.resolve();
  return createNotifications(
    uniq.map((userId) => ({
      orgId: rows.orgId,
      userId,
      productId: rows.productId,
      noteId: null,
      fromName: rows.fromName,
      text: rows.text,
    })),
  );
}

/** Cria uma discussão (encaminhamento ou assunto) com a 1ª mensagem. */
export async function createDiscussion(params: {
  orgId: string;
  kind: DiscussionKind;
  title: string;
  productId: string | null;
  assignedToId: string | null;
  assignedName: string | null;
  participantIds?: string[];
  createdById: string | null;
  createdByName: string | null;
  firstMessage?: string | null;
}): Promise<{ error?: string; id?: string }> {
  const participants = [
    ...new Set([
      ...(params.participantIds ?? []),
      ...(params.assignedToId ? [params.assignedToId] : []),
    ]),
  ];
  const { data, error } = await supabase
    .from('meeting_actions')
    .insert({
      org_id: params.orgId,
      kind: params.kind,
      product_id: params.productId,
      title: params.title,
      created_by: params.createdById,
      created_by_name: params.createdByName,
      assigned_to: params.assignedToId,
      assigned_name: params.assignedName,
      participant_ids: participants.length ? participants : null,
    })
    .select('id')
    .single();
  if (error) return { error: error.message };
  const id = (data as { id: string }).id;
  if (params.firstMessage?.trim()) {
    await insertMessage({
      orgId: params.orgId,
      actionId: id,
      authorId: params.createdById,
      authorName: params.createdByName,
      body: params.firstMessage.trim(),
    });
  }
  // Avisa todos os participantes (menos quem abriu).
  await notify({
    orgId: params.orgId,
    userIds: participants.filter((u) => u !== params.createdById),
    productId: params.productId,
    fromName: params.createdByName,
    text: `📌 ${params.kind === 'assunto' ? 'Assunto' : 'Encaminhamento'}: ${params.title}`,
  });
  return { id };
}

/** Compat: cria um encaminhamento de projeto com UM responsável. */
export async function createEncaminhamento(params: {
  orgId: string;
  productId: string | null;
  noteId: string | null;
  title: string;
  createdById: string | null;
  createdByName: string | null;
  assignedToId: string;
  assignedName: string | null;
}): Promise<{ error?: string; id?: string }> {
  return createDiscussion({
    orgId: params.orgId,
    kind: 'encaminhamento',
    title: params.title,
    productId: params.productId,
    assignedToId: params.assignedToId,
    assignedName: params.assignedName,
    createdById: params.createdById,
    createdByName: params.createdByName,
    firstMessage: params.title,
  });
}

/**
 * Cria UM encaminhamento com VÁRIOS participantes (ao marcar mais de uma pessoa
 * num comentário). Todos ficam no mesmo fio, recebem notificação e podem
 * comentar — em vez de uma tarefa duplicada por pessoa.
 */
export async function createEncaminhamentoMulti(params: {
  orgId: string;
  productId: string | null;
  title: string;
  createdById: string | null;
  createdByName: string | null;
  participants: { id: string; name: string | null }[];
}): Promise<{ error?: string; id?: string }> {
  const ids = [...new Set(params.participants.map((p) => p.id))];
  const names = params.participants.map((p) => p.name ?? '—').join(', ');
  return createDiscussion({
    orgId: params.orgId,
    kind: 'encaminhamento',
    title: params.title,
    productId: params.productId,
    assignedToId: null,
    assignedName: names || null,
    participantIds: ids,
    createdById: params.createdById,
    createdByName: params.createdByName,
    firstMessage: params.title,
  });
}

/** Nova mensagem no fio; avisa o outro lado (responsável e/ou quem abriu). */
export async function addMessage(params: {
  discussion: Discussion;
  authorId: string | null;
  authorName: string | null;
  body: string;
  attachmentUrl?: string | null;
  attachmentName?: string | null;
}): Promise<{ error?: string; id?: string }> {
  const d = params.discussion;
  const res = await insertMessage({
    orgId: d.orgId,
    actionId: d.id,
    authorId: params.authorId,
    authorName: params.authorName,
    body: params.body,
    attachmentUrl: params.attachmentUrl,
    attachmentName: params.attachmentName,
  });
  if (res.error) return res;
  // Avisa todo mundo do fio (quem abriu + participantes), menos quem escreveu.
  await notify({
    orgId: d.orgId,
    userIds: [d.createdById, d.assignedToId, ...d.participantIds].filter((u) => u !== params.authorId),
    productId: d.productId,
    fromName: params.authorName,
    text: `💬 ${params.authorName ?? 'Alguém'} comentou em "${d.title}"`,
  });
  return res;
}

/**
 * Marca pessoas num fio e direciona o "a fazer" a elas: entram em participant_ids
 * (passam a ver em "Minhas tarefas"), ficam no cabeçalho e recebem notificação.
 * Usa função SECURITY DEFINER (qualquer membro pode marcar).
 */
export async function addDiscussionMentions(params: {
  discussion: Discussion;
  newIds: string[]; // marcados (podem incluir quem já é participante)
  allNames: string; // nomes de todos (exibição no cabeçalho)
  fromId: string | null;
  fromName: string | null;
}): Promise<{ error?: string }> {
  const really = params.newIds.filter((i) => !params.discussion.participantIds.includes(i));
  const { error } = await supabase.rpc('add_discussion_participants', {
    p_action: params.discussion.id,
    p_ids: params.newIds,
    p_names: params.allNames || null,
  });
  if (error) return { error: error.message };
  await notify({
    orgId: params.discussion.orgId,
    userIds: really.filter((i) => i !== params.fromId),
    productId: params.discussion.productId,
    fromName: params.fromName,
    text: `📌 ${params.fromName ?? 'Alguém'} marcou você em "${params.discussion.title}" — responder`,
  });
  return {};
}

export async function finalizeDiscussion(id: string): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('meeting_actions')
    .update({ status: 'encerrado', closed_at: new Date().toISOString() })
    .eq('id', id);
  return { error: error?.message };
}

export async function reopenDiscussion(d: Discussion): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('meeting_actions')
    .update({ status: 'aberto', closed_at: null })
    .eq('id', d.id);
  if (error) return { error: error.message };
  await notify({
    orgId: d.orgId,
    userIds: [d.assignedToId, ...d.participantIds],
    productId: d.productId,
    fromName: d.createdByName,
    text: `↩️ Reaberto: ${d.title}`,
  });
  return {};
}

export async function deleteDiscussion(id: string): Promise<{ error?: string }> {
  const { error } = await supabase.from('meeting_actions').delete().eq('id', id);
  return { error: error?.message };
}

// ---- Leituras --------------------------------------------------------------
/** Encaminhamentos abertos onde o usuário é responsável ou dono. */
export async function loadMyEncaminhamentos(userId: string): Promise<Discussion[]> {
  const { data } = await supabase
    .from('meeting_actions')
    .select(HCOLS)
    .eq('kind', 'encaminhamento')
    .eq('status', 'aberto')
    .or(`assigned_to.eq.${userId},created_by.eq.${userId},participant_ids.cs.{${userId}}`)
    .order('created_at', { ascending: false });
  return attachMessages(((data ?? []) as Record<string, unknown>[]).map(mapHeader));
}

/** Encaminhamentos abertos da org (visão da reunião). */
export async function loadOpenEncaminhamentos(orgId: string): Promise<Discussion[]> {
  const { data } = await supabase
    .from('meeting_actions')
    .select(HCOLS)
    .eq('org_id', orgId)
    .eq('kind', 'encaminhamento')
    .eq('status', 'aberto')
    .order('created_at', { ascending: false });
  return attachMessages(((data ?? []) as Record<string, unknown>[]).map(mapHeader));
}

/** Encaminhamentos de um projeto (abertos e encerrados, para o histórico). */
export async function loadEncaminhamentosForProduct(productId: string): Promise<Discussion[]> {
  const { data } = await supabase
    .from('meeting_actions')
    .select(HCOLS)
    .eq('product_id', productId)
    .eq('kind', 'encaminhamento')
    .order('created_at', { ascending: false });
  return attachMessages(((data ?? []) as Record<string, unknown>[]).map(mapHeader));
}

/** Assuntos avulsos (menu "Outros assuntos"). */
export async function loadAssuntos(
  orgId: string,
  opts: { includeClosed?: boolean } = {},
): Promise<Discussion[]> {
  let q = supabase.from('meeting_actions').select(HCOLS).eq('org_id', orgId).eq('kind', 'assunto');
  if (!opts.includeClosed) q = q.eq('status', 'aberto');
  const { data } = await q.order('created_at', { ascending: false });
  return attachMessages(((data ?? []) as Record<string, unknown>[]).map(mapHeader));
}
